import { useState } from 'react';
import { doc, updateDoc } from 'firebase/firestore';
import { toast } from 'sonner';
import { Vote, Lock, Eye, EyeOff } from 'lucide-react';

import { db } from '../../firebase/config';
import { Switch } from '../ui/switch';
import { Separator } from '../ui/separator';
import { cn } from '../../lib/utils';

/**
 * Admin controls for community voting on a mapping tournament:
 * - Open/close voting. Voting is considered OPEN unless
 *   `tournament.votingOpen === false`, so existing tournaments without the
 *   field keep their current (open) behaviour.
 * - Reveal/hide the community average. The average is HIDDEN unless
 *   `tournament.communityAverageVisible === true` — voters always see their
 *   own votes, but the aggregated result only appears once revealed.
 */
const MappingVotingControl = ({ tournament, onUpdated }) => {
  const isOpen = tournament?.votingOpen !== false;
  const averageVisible = tournament?.communityAverageVisible === true;
  const [saving, setSaving] = useState(false);

  const updateField = async (data, successMessage) => {
    if (saving) return;
    setSaving(true);
    try {
      await updateDoc(doc(db, 'tournaments', tournament.id), data);
      toast.success(successMessage);
      if (onUpdated) await onUpdated();
    } catch (error) {
      toast.error('Could not update voting settings.', { description: error.message });
    } finally {
      setSaving(false);
    }
  };

  const setVotingOpen = (open) =>
    updateField(
      { votingOpen: open },
      open ? 'Community voting opened.' : 'Community voting closed.'
    );

  const setAverageVisible = (visible) =>
    updateField(
      { communityAverageVisible: visible },
      visible ? 'Community average revealed.' : 'Community average hidden.'
    );

  return (
    <div className="flex flex-col gap-4">
      <ControlRow
        active={isOpen}
        activeIcon={Vote}
        inactiveIcon={Lock}
        title={`Community voting is ${isOpen ? 'open' : 'closed'}`}
        description={
          isOpen
            ? 'Visitors can rate every entry. Close voting to lock in the final community scores.'
            : 'Voting is locked — visitors see a “voting is closed” notice and cannot rate entries.'
        }
        stateLabel={isOpen ? 'Open' : 'Closed'}
        disabled={saving}
        onToggle={setVotingOpen}
        ariaLabel="Toggle community voting"
      />

      <Separator />

      <ControlRow
        active={averageVisible}
        activeIcon={Eye}
        inactiveIcon={EyeOff}
        title={`Community average is ${averageVisible ? 'visible' : 'hidden'}`}
        description={
          averageVisible
            ? 'Everyone can see the average community rating per entry and its share of the leaderboard total.'
            : 'Only individual votes are visible to their voters. Reveal to publish the averages and add them to the leaderboard.'
        }
        stateLabel={averageVisible ? 'Visible' : 'Hidden'}
        disabled={saving}
        onToggle={setAverageVisible}
        ariaLabel="Toggle community average visibility"
      />
    </div>
  );
};

const ControlRow = ({
  active,
  activeIcon: ActiveIcon,
  inactiveIcon: InactiveIcon,
  title,
  description,
  stateLabel,
  disabled,
  onToggle,
  ariaLabel,
}) => (
  <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
    <div className="flex items-start gap-3">
      <div
        className={cn(
          'mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-md border',
          active ? 'border-heat/60 text-heat' : 'border-border/60 text-muted-foreground'
        )}
      >
        {active ? <ActiveIcon className="h-4 w-4" /> : <InactiveIcon className="h-4 w-4" />}
      </div>
      <div>
        <p className="text-sm font-medium">{title}</p>
        <p className="mt-0.5 text-sm text-muted-foreground">{description}</p>
      </div>
    </div>
    <div className="flex shrink-0 items-center gap-3">
      <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
        {stateLabel}
      </span>
      <Switch
        checked={active}
        disabled={disabled}
        onCheckedChange={onToggle}
        aria-label={ariaLabel}
      />
    </div>
  </div>
);

export default MappingVotingControl;
